import { ApplicationRef, ComponentFactoryResolver, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TtScreenBlockComponent } from './tt-screen-block.component';

// A small helper to generate random IDs if needed
function uuid(): string {
    return Math.random().toString(36).substr(2, 9);
}

@Injectable({
    providedIn: 'root'
})
export class TtScreenBlockService {
    /**
     * Whether the screen is currently blocked (true/false).
     * In Angular, we typically expose this as a BehaviorSubject
     * so components can subscribe to changes.
     */
    private blockedSubject = new BehaviorSubject<boolean>(false);
    /** Expose as an Observable for read-only subscribers */
    isBlocked$: Observable<boolean> = this.blockedSubject.asObservable();

    /**
     * Tracks the current template ID (e.g., '', 'progress', 'message').
     * The component will switch templates or show/hide progress bar, etc.
     */
    private templateIdSubject = new BehaviorSubject<string>('');
    templateId$: Observable<string> = this.templateIdSubject.asObservable();

    /**
     * A message to display under the spinner (e.g., "Loading...").
     */
    private messageSubject = new BehaviorSubject<string>('');
    message$: Observable<string> = this.messageSubject.asObservable();

    /**
     * A numeric progress value (0–100).
     */
    private progressSubject = new BehaviorSubject<number>(0);
    progress$: Observable<number> = this.progressSubject.asObservable();

    /**
     * Whether we've already attached the TtScreenBlockComponent to body.
     * We only want to do this once.
     */
    private screenBlockAttached = false;

    constructor(
        private appRef: ApplicationRef,
        private componentFactoryResolver: ComponentFactoryResolver,
        private injector: Injector
    ) { }

    /**
   * Check if <tt-screen-block> is already present. If not,
   * dynamically create and append it to the <body>.
   */
    ensureTtScreenBlockAttached(): void {
        if (this.screenBlockAttached) {
            return;
        }

        const existingElement = document.querySelector('tt-screen-block');

        if (!existingElement) {
            const componentFactory =
                this.componentFactoryResolver.resolveComponentFactory(TtScreenBlockComponent);

            // Create the component using the factory
            const componentRef = componentFactory.create(this.injector);

            // Tell Angular to begin tracking this component’s view
            this.appRef.attachView(componentRef.hostView);

            // Append the component’s root DOM element to body
            document.body.appendChild(componentRef.location.nativeElement);
        }

        this.screenBlockAttached = true;
    }

    /** Start blocking the screen, optionally specify a template ID. */
    start(templateId?: string): void {
        this.ensureTtScreenBlockAttached();

        this.templateIdSubject.next(templateId || '');

        this.blockedSubject.next(true);
    }

    /** Stop blocking the screen. */
    stop(): void {
        this.blockedSubject.next(false);
    }

    /** Set a message (e.g., "Loading user data..."). */
    setMessage(message: string): void {
        this.messageSubject.next(message);
    }

    /** Set numeric progress (e.g., 25). */
    setProgress(progress: number): void {
        this.progressSubject.next(progress);
    }

    /** Set both progress and message at once. */
    setProgressAndMessage(progress: number, message: string): void {
        this.setProgress(progress);
        this.setMessage(message);
    }

    /** For advanced usage if you still want an ID-based handler approach. */
    on(event: 'message' | 'progress', handler: (val: any) => void): () => void {
        // Not typical for Angular, but here’s a basic example:
        const id = uuid();
        // You could manually subscribe in code if you want to attach/detach
        let sub: any;
        if (event === 'message') {
            sub = this.message$.subscribe(handler);
        } else if (event === 'progress') {
            sub = this.progress$.subscribe(handler);
        }
        return () => {
            if (sub) {
                sub.unsubscribe();
            }
        };
    }
}
